import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, RotateCcw, Save } from 'lucide-react';
import i18n from '../../i18n';
import apiClient from '../../api/client';
import { useToast } from '../../components/ui/Toast';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';

/* Dynamic document template editor (#1). Edit any document's layout/content per scope
 * (GLOBAL or B2B/B2C segment) and see a live preview, without redeploying. Stores a delta
 * patch on top of the code-defined default; "Restore default" removes it. */

type DocType =
  | 'INVOICE'
  | 'QUOTE'
  | 'CREDIT_NOTE'
  | 'DELIVERY_NOTE'
  | 'DELIVERY_NOTE_CLIENT'
  | 'ORDER'
  | 'TICKET'
  | 'DEPOSIT_INVOICE'
  | 'PICKUP_SLIP';
type Scope = 'GLOBAL' | 'B2B' | 'B2C';

interface Column {
  key: string;
  label: string;
  visible: boolean;
}
interface Config {
  title: string;
  labels: Record<string, string>;
  columns: Column[];
  style: { accent: string };
  blocks: Record<string, string>;
  vatMentions: Record<string, string>;
}
type Patch = Partial<Config>;
interface DocTypePayload {
  default: Config;
  patches: Record<Scope, Patch | null>;
  resolved: Record<Scope, Config>;
}

/* In handover order: what the counter hands over first comes first. Text carrying a figure keeps
 * its {tokens} ({paid}, {date}, {pickupDays}…) — the system fills them in. */
const DOC_TYPES: DocType[] = [
  'TICKET',
  'DEPOSIT_INVOICE',
  'INVOICE',
  'DELIVERY_NOTE_CLIENT',
  'DELIVERY_NOTE',
  'PICKUP_SLIP',
  'ORDER',
  'QUOTE',
  'CREDIT_NOTE',
];
const SCOPES: Scope[] = ['GLOBAL', 'B2B', 'B2C'];

const EMPTY_DRAFTS: Record<Scope, Patch> = { GLOBAL: {}, B2B: {}, B2C: {} };
/** Top-level config keys a patch can override — drives the "what differs" summary. */
const PATCH_FIELDS: (keyof Config)[] = [
  'title',
  'style',
  'columns',
  'labels',
  'blocks',
  'vatMentions',
];

export default function DocumentTemplates() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [docType, setDocType] = useState<DocType>('INVOICE');
  const [scope, setScope] = useState<Scope>('GLOBAL');
  const [payload, setPayload] = useState<DocTypePayload | null>(null);
  // One in-flight draft per scope. Switching scope used to refetch and blow away whatever the
  // operator had typed; the payload already carries all three scopes, so switching is local.
  const [drafts, setDrafts] = useState<Record<Scope, Patch>>(EMPTY_DRAFTS);
  const [previewHtml, setPreviewHtml] = useState('');

  const patch = drafts[scope];
  const setPatch = useCallback(
    (update: Patch | ((prev: Patch) => Patch)) =>
      setDrafts((d) => ({
        ...d,
        [scope]: typeof update === 'function' ? update(d[scope]) : update,
      })),
    [scope]
  );

  // The inheritance base the patch layers on: GLOBAL inherits the code default; segments
  // inherit the resolved GLOBAL config.
  const base: Config | null = useMemo(() => {
    if (!payload) return null;
    return scope === 'GLOBAL' ? payload.default : payload.resolved.GLOBAL;
  }, [payload, scope]);

  const load = useCallback(
    async (dt: DocType) => {
      try {
        const { data } = await apiClient.get<DocTypePayload>(`/admin/document-templates/${dt}`);
        setPayload(data);
        setDrafts({
          GLOBAL: data.patches.GLOBAL ?? {},
          B2B: data.patches.B2B ?? {},
          B2C: data.patches.B2C ?? {},
        });
      } catch (err) {
        showApiError(err);
      }
    },
    [showApiError]
  );

  // Only the document type needs a round-trip — one payload holds every scope.
  useEffect(() => {
    void load(docType);
  }, [docType, load]);

  // Debounced live preview whenever the patch changes.
  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const { data } = await apiClient.post<string>(
          `/admin/document-templates/${docType}/preview`,
          { scope, config: patch },
          { headers: { 'X-Command-Id': crypto.randomUUID() }, responseType: 'text' }
        );
        setPreviewHtml(data);
      } catch {
        /* preview is best-effort */
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [docType, scope, patch]);

  const eff = (f: keyof Config): Config[keyof Config] | undefined =>
    (patch as Config)[f] ?? base?.[f];

  const setTitle = (v: string) => setPatch((p) => ({ ...p, title: v }));
  const setAccent = (v: string) => setPatch((p) => ({ ...p, style: { accent: v } }));
  const setLabel = (k: string, v: string) =>
    setPatch((p) => ({ ...p, labels: { ...(p.labels ?? {}), [k]: v } }));
  const setBlock = (k: string, v: string) =>
    setPatch((p) => ({ ...p, blocks: { ...(p.blocks ?? {}), [k]: v } }));
  const setColumns = (cols: Column[]) => setPatch((p) => ({ ...p, columns: cols }));

  const cols: Column[] = (patch.columns ?? base?.columns ?? []) as Column[];
  const toggleCol = (i: number) =>
    setColumns(cols.map((c, j) => (j === i ? { ...c, visible: !c.visible } : c)));
  const renameCol = (i: number, label: string) =>
    setColumns(cols.map((c, j) => (j === i ? { ...c, label } : c)));
  const moveCol = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= cols.length) return;
    const next = cols.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setColumns(next);
  };

  const save = async () => {
    try {
      const { data } = await apiClient.put<DocTypePayload>(
        `/admin/document-templates/${docType}/${scope}`,
        patch,
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      setPayload(data);
      // Only this scope's draft is reconciled with what the server stored — an unsaved draft on
      // another tab must survive its sibling being saved.
      setPatch(data.patches[scope] ?? {});
      push({
        tone: 'success',
        message: t('docTemplates.toast.saved', { doc: labelOf(docType), scope }),
      });
    } catch (err) {
      showApiError(err);
    }
  };

  const restore = async () => {
    // Nothing is stored yet — "restore" just throws away the unsaved edits, no round-trip.
    if (!payload?.patches[scope]) {
      setPatch({});
      return;
    }
    try {
      const { data } = await apiClient.delete<DocTypePayload>(
        `/admin/document-templates/${docType}/${scope}`,
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      setPayload(data);
      setPatch({});
      push({
        tone: 'success',
        message: t('docTemplates.toast.restored', { doc: labelOf(docType), scope }),
      });
    } catch (err) {
      showApiError(err);
    }
  };

  const labels = base ? Object.keys({ ...base.labels, ...(patch.labels ?? {}) }) : [];
  const blocks = base ? Object.keys({ ...base.blocks, ...(patch.blocks ?? {}) }) : [];
  const hasPatch = Boolean(payload?.patches[scope]);

  /** Which scopes carry unsaved edits — the reason switching tabs must not refetch. */
  const dirty = useMemo(() => {
    const out = {} as Record<Scope, boolean>;
    for (const s of SCOPES) {
      out[s] = JSON.stringify(drafts[s]) !== JSON.stringify(payload?.patches[s] ?? {});
    }
    return out;
  }, [drafts, payload]);

  /** Human list of what this scope overrides, so an inherited scope never looks like a dead tab. */
  const overriddenFields = PATCH_FIELDS.filter((f) => {
    const v = patch[f];
    return v !== undefined && !(v && typeof v === 'object' && Object.keys(v).length === 0);
  }).map((f) => t(`docTemplates.fields.${f}`, { defaultValue: f }));

  return (
    <div className="w-full">
      <PageHeader
        title={t('docTemplates.title')}
        description={t('docTemplates.description')}
        actions={
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={!hasPatch && !dirty[scope]}
              onClick={restore}
              title={
                hasPatch
                  ? scope === 'GLOBAL'
                    ? t('docTemplates.actions.deleteOverrideTitle', { scope })
                    : t('docTemplates.actions.deleteOverrideTitleSegment', { scope })
                  : t('docTemplates.actions.discardTitle')
              }
            >
              <RotateCcw className="h-4 w-4" />
              {hasPatch
                ? scope === 'GLOBAL'
                  ? t('docTemplates.actions.restoreDefault')
                  : t('docTemplates.actions.removeOverride', { scope })
                : t('docTemplates.actions.discardChanges')}
            </Button>
            <Button variant="success" size="sm" onClick={save}>
              <Save className="h-4 w-4" />
              {t('docTemplates.actions.save')}
              {dirty[scope] ? ' •' : ''}
            </Button>
          </div>
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Select
          className="w-48"
          value={docType}
          onChange={(e) => setDocType(e.target.value as DocType)}
        >
          {DOC_TYPES.map((d) => (
            <option key={d} value={d}>
              {labelOf(d)}
            </option>
          ))}
        </Select>
        <div className="inline-flex divide-x divide-border overflow-hidden rounded-md border border-input">
          {SCOPES.map((s) => {
            const stored = Boolean(payload?.patches[s]);
            return (
              <button
                key={s}
                onClick={() => setScope(s)}
                title={
                  s === 'GLOBAL'
                    ? t('docTemplates.scopes.globalTitle')
                    : t('docTemplates.scopes.segmentTitle', { scope: s })
                }
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
                  scope === s
                    ? 'bg-primary font-medium text-primary-foreground'
                    : 'bg-card text-muted-foreground hover:bg-accent hover:text-foreground'
                }`}
              >
                {s}
                {/* Overridden vs inherited, and whether the tab holds unsaved edits — without
                    these, three tabs resolving to the same config look like a dead control. */}
                {stored && (
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${scope === s ? 'bg-primary-foreground' : 'bg-primary'}`}
                    title={t('docTemplates.scopes.storedOverride')}
                  />
                )}
                {dirty[s] && (
                  <span className="text-[10px] uppercase tracking-wide opacity-80">
                    {t('docTemplates.scopes.unsaved')}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Says out loud what the selected scope currently is: inherited or overriding. */}
      <p className="mb-4 text-xs text-muted-foreground">
        {scope === 'GLOBAL' ? (
          <>
            <span className="font-medium text-foreground">GLOBAL</span>{' '}
            {t('docTemplates.summary.globalBefore', { doc: labelOf(docType) })}
          </>
        ) : overriddenFields.length === 0 ? (
          <>
            <span className="font-medium text-foreground">{scope}</span>{' '}
            {t('docTemplates.summary.inheritsBefore')}{' '}
            <span className="font-medium text-foreground">GLOBAL</span>{' '}
            {t('docTemplates.summary.inheritsAfter', { scope })}
          </>
        ) : (
          <>
            <span className="font-medium text-foreground">{scope}</span>{' '}
            {t('docTemplates.summary.overridesBefore')}{' '}
            <span className="font-medium text-foreground">{overriddenFields.join(', ')}</span>.{' '}
            {t('docTemplates.summary.overridesAfter')}
          </>
        )}
      </p>

      <div className="grid grid-cols-2 gap-4">
        {/* Editor */}
        <div className="space-y-4 text-sm">
          <Field label={t('docTemplates.editor.title')}>
            <Input
              value={(eff('title') as string) ?? ''}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>
          <Field label={t('docTemplates.editor.accent')}>
            <Input
              type="color"
              className="h-9 w-16 cursor-pointer p-1"
              value={base ? (patch.style?.accent ?? base.style.accent) : '#000000'}
              onChange={(e) => setAccent(e.target.value)}
            />
          </Field>

          <Section title={t('docTemplates.editor.columns')}>
            {cols.map((c, i) => (
              <div key={c.key} className="flex items-center gap-2 py-1">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-input accent-primary"
                  checked={c.visible}
                  onChange={() => toggleCol(i)}
                />
                <Input
                  className="h-8 flex-1"
                  value={c.label}
                  onChange={(e) => renameCol(i, e.target.value)}
                />
                <span className="w-24 truncate text-xs text-muted-foreground/60" title={c.key}>
                  {c.key}
                </span>
                {/* Bordered pair, joined into one control: the reorder affordance reads as a
                    single widget and picks up the same border token as the field beside it. */}
                <div className="inline-flex shrink-0 divide-x divide-border overflow-hidden rounded-md border border-input">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-none"
                    title={t('docTemplates.editor.moveUp')}
                    onClick={() => moveCol(i, -1)}
                    disabled={i === 0}
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-none"
                    title={t('docTemplates.editor.moveDown')}
                    onClick={() => moveCol(i, 1)}
                    disabled={i === cols.length - 1}
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </Section>

          <Section title={t('docTemplates.editor.labels')}>
            {labels.map((k) => (
              <div key={k} className="flex items-center gap-2 py-1">
                <span className="w-32 truncate text-xs text-muted-foreground" title={k}>
                  {k}
                </span>
                <Input
                  className="h-8 flex-1"
                  value={patch.labels?.[k] ?? base?.labels[k] ?? ''}
                  onChange={(e) => setLabel(k, e.target.value)}
                />
              </div>
            ))}
          </Section>

          {blocks.length > 0 && (
            <Section title={t('docTemplates.editor.blocks')}>
              {blocks.map((k) => (
                <div key={k} className="py-1">
                  <div className="mb-1 text-xs text-muted-foreground">{k}</div>
                  <Textarea
                    rows={2}
                    value={patch.blocks?.[k] ?? base?.blocks[k] ?? ''}
                    onChange={(e) => setBlock(k, e.target.value)}
                  />
                </div>
              ))}
            </Section>
          )}
        </div>

        {/* Live preview */}
        <div className="border rounded bg-white overflow-hidden h-[700px] sticky top-4">
          <iframe
            title={t('docTemplates.editor.preview')}
            srcDoc={previewHtml}
            className="w-full h-full"
          />
        </div>
      </div>
    </div>
  );
}

function labelOf(dt: DocType): string {
  return i18n.t(`docTemplates.docTypes.${dt}`, { defaultValue: dt });
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-muted-foreground mb-1">{label}</span>
      {children}
    </label>
  );
}
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border rounded-lg bg-card overflow-hidden">
      <header className="px-3 py-1.5 border-b text-xs uppercase tracking-wide text-muted-foreground">
        {title}
      </header>
      <div className="px-3 py-2">{children}</div>
    </section>
  );
}
